/**
 * WHAT HAPPENED TO THE WRITE — one classification, used by every screen that performs one.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SIX ANSWERS, AND THE WHOLE POINT IS THAT THEY DO NOT COLLAPSE INTO "אירעה תקלה".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The Owner's direction is that materially different states must not become a generic 403/409/500
 * when the server already knows the actionable reason. That direction is only kept if the CLIENT
 * keeps the answers apart too — and it will not stay kept if each of a dozen screens writes its own
 * `catch` block. The first one that writes `catch { setError(true) }` erases the whole contract for
 * its own screen, quietly, and nothing fails.
 *
 * So this module is the one place an error from `client.ts` becomes a product state:
 *
 *   denied       the GATE refused. A `DenyBody`, rendered by `<DenialNotice>`, uniform outside the
 *                tenant and named inside it. This is the only member that is an access decision.
 *   stepUp       the handler refused because the session is stale or a factor is missing. NOT a
 *                denial; it carries `need`, which names the door to open.
 *   conflict     the gate ALLOWED it and the write still could not happen. Carries the actionable
 *                half — the excess, the missing fields, the permitted states, the count and limit.
 *   notFound     the object is not there, or is not visible to this caller, and the product does not
 *                distinguish the two on purpose.
 *   unreachable  the request never got an answer. Distinct from a failure BECAUSE A WRITE IS NEVER
 *                RETRIED: the client cannot know whether the server committed, so the honest sentence
 *                is "we could not reach the server", never "it did not happen".
 *   error        something else. The only thing shown is a support reference (09 §9.1 V3).
 *
 * ── WHAT THIS MODULE MAY NOT DO ─────────────────────────────────────────────────────────────────
 *
 * It may not decide permission, and it cannot: it never sees a capability, a role or a session. It
 * reads which ERROR CLASS the client produced and copies the server's own body across. Every screen
 * then hands the result to a mapper that turns it into Hebrew — no screen renders a member of a body.
 */

import { ApiError, ConflictError, DeniedError, NotFoundError, StepUpRequiredError } from './client';
import type { ConflictBody } from './client';
import type { DenyBody, StepUpNeed } from './contract';
import { stepUpNeedOf } from './stepUpText';

export type ActionFailure =
  | { readonly kind: 'denied'; readonly body: DenyBody }
  | { readonly kind: 'stepUp'; readonly need: StepUpNeed | null }
  | { readonly kind: 'conflict'; readonly body: ConflictBody }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'unreachable' }
  | { readonly kind: 'error'; readonly requestUid: string };

/**
 * Classifies a thrown value. Total by construction: anything unrecognised becomes `unreachable`,
 * which is the SAFE default for a write — it claims nothing about whether the server committed.
 */
export function classifyActionError(err: unknown): ActionFailure {
  if (err instanceof StepUpRequiredError) {
    // `need` is validated into the union here rather than trusted: a value this build has no Hebrew
    // for becomes `null`, and the screen then says less instead of naming the wrong screen.
    return { kind: 'stepUp', need: stepUpNeedOf({ need: err.need }) };
  }
  if (err instanceof DeniedError) return { kind: 'denied', body: err.body };
  if (err instanceof ConflictError) return { kind: 'conflict', body: err.body };
  if (err instanceof NotFoundError) return { kind: 'notFound' };
  // `status === 0` is the transport failure `NetworkError` carries.
  if (err instanceof ApiError && err.status === 0) return { kind: 'unreachable' };
  if (err instanceof ApiError) return { kind: 'error', requestUid: err.requestUid };
  return { kind: 'unreachable' };
}

export interface ActionFailureText {
  readonly titleHe: string;
  readonly bodyHe: string;
  readonly nextStepHe: string;
}

/**
 * The two failures that have no server-supplied detail, in words.
 *
 * `unreachable` is the one worth reading twice. It does NOT say "the action did not happen", because
 * nobody knows that: the connection died and the write was not retried, precisely because a repeated
 * write is the worse outcome. So it says what is true — no answer arrived — and tells the user to
 * look before repeating.
 */
export const UNREACHABLE_HE: ActionFailureText = {
  titleHe: 'לא התקבלה תשובה מהשרת',
  bodyHe:
    'הבקשה נשלחה ולא חזרה ממנה תשובה, ולכן לא ניתן לדעת אם היא בוצעה. המערכת אינה שולחת פעולה כזו ' +
    'שוב מעצמה — שליחה כפולה של פעולה שכבר בוצעה היא התוצאה הגרועה יותר.',
  nextStepHe: 'יש לרענן את המסך ולבדוק את המצב העדכני לפני ניסיון נוסף.',
};

export const NOT_FOUND_HE: ActionFailureText = {
  titleHe: 'הפריט אינו זמין',
  // Careful not to confirm or deny that the object exists anywhere: that is the gate's disclosure
  // rule, and a screen that leaked it would undo it.
  bodyHe: 'לא הצלחנו למצוא את הפריט שאליו מפנה הפעולה, ולכן לא נעשה בו שינוי.',
  nextStepHe: 'רענון המסך ובחירה מתוך הרשימה המעודכנת.',
};
